"use client";

import React from "react";
import {zodResolver} from '@hookform/resolvers/zod';
import {Controller, useForm} from 'react-hook-form';
import {z} from 'zod';
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form';
import {Input} from '@/components/ui/input';
import {Checkbox} from "@/components/ui/checkbox";
import {useRouter} from "next/navigation";
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from "@/components/ui/select";
import {provinces} from "@/components/FormLead";
import {getLeadSourceValue} from "@/utils/leadSource";

const formSchema = z.object({
    nome: z.string().min(1, {message: 'Il nome è obbligatorio.'}),
    cognome: z.string().min(1, {message: 'Il cognome è obbligatorio.'}),
    cv: z
        .any()
        .refine((files) => files?.[0]?.size <= 5 * 1024 * 1024, { message: 'Il file deve essere inferiore a 5 MB.' })
        .refine((files) => ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'].includes(files?.[0]?.type), { message: 'Formato file non supportato.' }),
    email: z.string().email({message: 'Inserisci un indirizzo email valido.'}).min(1, {message: 'L\'email è obbligatoria.'}),
    telefono: z.string().min(10, {message: 'Il numero di telefono deve avere almeno 10 cifre.'}).min(1, {message: 'Il telefono è obbligatorio.'}),
    provincia: z.string().min(1, {message: 'La provincia è obbligatoria.'}),
    terms: z.boolean().refine(val => val, {message: 'Devi accettare i termini e le condizioni.'}),
});


const FormLavoraConNoi = () => {
    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = React.useState(false);

    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onTouched',
        defaultValues: {
            nome: '',
            cognome: '',
            cv: '',
            email: '',
            telefono: '',
            provincia: '',
            terms: false,
        }
    });

    const onSubmit = async (data: any) => {
        try {

            // controlla che il form sia valido


            console.log('Dati inviati:', data);
            setIsSubmitting(true); // Imposta lo stato di loading

            // Componi il FormData
            const formData = new FormData();
            formData.append('nome', data.nome);
            formData.append('cognome', data.cognome);
            formData.append('email', data.email);
            formData.append('telefono', data.telefono);
            formData.append('provincia', data.provincia);
            formData.append('fonte', getLeadSourceValue())
            if (data.cv && data.cv[0]) {
                formData.append('cv', data.cv[0]); // Aggiungi il file al FormData
            }

            console.log('Invio dei dati al server:', formData);

            const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/api/wsdl/academy`, {
                method: 'POST',
                body: formData,
            });

            if (!response.ok) {
                throw new Error('Errore nella richiesta: ' + response.status);
            }

            const result = await response.json();
            console.log('Risultato della richiesta:', result);

            setIsSubmitting(false); // Disabilita lo stato di loading

            if (result.status === 'success') {
                form.reset();
                const descrizione = 'Grazie per averci contattato. Ti risponderemo al più presto.';
                await router.push(`/thank-you-page/lavora-con-noi?descrizione=${encodeURIComponent(descrizione)}&email=${encodeURIComponent(data.email)}&nome=${encodeURIComponent(data.nome)}&telefono=${encodeURIComponent(data.telefono)}`);
            } else {
                console.error('Errore durante l\'invio dei dati:', result);
            }
        } catch (error) {
            console.error('Errore durante la richiesta:', error);
            setIsSubmitting(false); // Disabilita lo stato di loading in caso di errore
        }
    };


    return (
        <section id="form-lavora-con-noi" className="my-24">
            <div className="flex flex-col gap-y-4 py-8">
                <h1 className="text-4xl font-bold">Candidati ora</h1>
                <p className="text-xl text-[#5B5B5B] font-normal">
                    Compila il modulo ed allega il tuo CV. <br/>
                    Non perdere l'occasione di fare la differenza!
                </p>
            </div>


            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-4">
                        {/* Nome */}
                        <FormField control={form.control} name="nome"
                                   render={({field, fieldState}) => (
                                       <FormItem>
                                           <FormLabel>Nome</FormLabel>
                                           <FormControl>
                                               <Input {...field} className="border-primary" />
                                           </FormControl>
                                           <FormMessage>{fieldState.error?.message}</FormMessage>
                                       </FormItem>
                                   )}
                        />
                        {/* Cognome */}
                        <FormField control={form.control} name="cognome"
                                   render={({field, fieldState}) => (
                                       <FormItem>
                                           <FormLabel>Cognome</FormLabel>
                                           <FormControl>
                                               <Input {...field} className="border-primary" />
                                           </FormControl>
                                           <FormMessage>{fieldState.error?.message}</FormMessage>
                                       </FormItem>
                                   )}
                        />
                    </div>

                    <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-4">
                        {/* Email */}
                        <FormField control={form.control} name="email"
                                   render={({field, fieldState}) => (
                                       <FormItem>
                                           <FormLabel>Email</FormLabel>
                                           <FormControl>
                                               <Input {...field} className="border-primary"/>
                                           </FormControl>
                                           <FormMessage>{fieldState.error?.message}</FormMessage>
                                       </FormItem>
                                   )}
                        />
                        {/* Telefono */}
                        <FormField control={form.control} name="telefono"
                                   render={({field, fieldState}) => (
                                       <FormItem>
                                           <FormLabel>Telefono</FormLabel>
                                           <FormControl>
                                               <Input {...field} className="border-primary"/>
                                           </FormControl>
                                           <FormMessage>{fieldState.error?.message}</FormMessage>
                                       </FormItem>
                                   )}
                        />
                    </div>

                    <div className="grid grid-cols-2 max-sm:grid-cols-1 gap-4">


                        <FormField
                            control={form.control}
                            name="provincia"
                            render={({field, fieldState}) => (
                                <FormItem>
                                    <FormLabel>Provincia</FormLabel>
                                    <FormControl>
                                        <Controller
                                            control={form.control}
                                            name="provincia"
                                            render={({field}) => (
                                                <Select
                                                    {...field}
                                                    onValueChange={(value) => field.onChange(value)}
                                                    value={field.value}
                                                >
                                                    <SelectTrigger className="border-primary">
                                                        <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        {provinces.map((province) => (
                                                            <SelectItem key={province.code} value={province.code}>
                                                                {province.name}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            )}
                                        />
                                    </FormControl>
                                    <FormMessage>{fieldState.error?.message}</FormMessage>
                                </FormItem>
                            )}
                        />


                        {/* CV */}
                        <FormField control={form.control} name="cv"
                                   render={({field, fieldState}) => (
                                       <FormItem>
                                           <FormLabel>CV  (max 5 MB)</FormLabel>
                                           <FormControl>
                                               <Input type="file" accept=".pdf,.doc,.docx" // Passa i file a React Hook Form
                                                      onChange={(e) => field.onChange(e.target.files)}
                                                      className="border-primary text-primary" max-size="5242880"/>
                                           </FormControl>
                                           <FormMessage>{fieldState.error?.message}</FormMessage>
                                       </FormItem>
                                   )}
                        />
                    </div>

                    {/* Terms */}
                    <div className="flex items-center">
                        <FormField control={form.control} name="terms"
                                   render={({field, fieldState}) => (
                                       <FormItem>
                                           <FormControl>
                                               <Checkbox className="text-white" checked={field.value}
                                                         onCheckedChange={field.onChange}/>
                                           </FormControl>
                                           <FormMessage>{fieldState.error?.message}</FormMessage>
                                       </FormItem>
                                   )}
                        />
                        <label htmlFor="terms" className="ml-2 cursor-pointer">
                            Accetto i <a href="https://www.iubenda.com/termini-e-condizioni/31103949"
                                         className="text-primary underline">termini e condizioni</a>.
                        </label>
                    </div>

                    <button type="submit" disabled={isSubmitting}
                            className={`bg-gradient-custom px-6 py-2 min-w-[200px] rounded-lg text-white text-center font-bold ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}>
                        {isSubmitting ? 'Invio...' : 'Invia'}
                    </button>
                </form>
            </Form>
        </section>
    )
}

export default FormLavoraConNoi;
